import {
  DimensionSource,
  TableByCampaign,
} from '@highimpact-frontend/api-types';
import {
  DateTimeRangePickerComponent,
  ErrorStatus,
} from '@highimpact/dashboard-ui';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import { addLocalOffsetAndFormatToLocalString } from '../../../../internal/utils/date';
import {
  ensureConfiguredTableByCampaign,
  getEndDate,
  getStartDate,
  isValid,
  setElementIndependentSourcesFromResolvedSources,
  setEndDate,
  setStartDate,
} from '../../../../state/dashboard-element-operations';
import { getDefaultIndependentDimensionKeys } from '../shared/buildWidgetDataSourceRequest';
import {
  buildIndependentDimensionSelectionFromElement,
  dimensionSelectionToAxisSources,
  resolveIndependentDimensionSelectionFromCatalog,
  syncIndependentDimensionSelectionFromCatalog,
} from '../shared/independentDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSection,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import { areAxisSourceSelectionsEqual } from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

type DateRange = [Date, Date] | null;

const TABLE_BY_CAMPAIGN_DIMENSION_KEYS =
  getDefaultIndependentDimensionKeys('table_by_campaign');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const TABLE_BY_CAMPAIGN_DIMENSION_KEY = TABLE_BY_CAMPAIGN_DIMENSION_KEYS[0]!;

function TableByCampaignForm({
  config,
  project_id,
  site_id,
  campaigns,
  start_time,
  end_time,
  onSubmit,
  onChange,
}: MigratedPlotFormProps) {
  const tableScope = useMemo(
    () => ({
      start_date: start_time.toISOString(),
      end_date: end_time.toISOString(),
    }),
    [start_time, end_time]
  );
  const ensureConfigured = useCallback(
    (element: typeof config) =>
      ensureConfiguredTableByCampaign(element, tableScope),
    [tableScope]
  );
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'table_by_campaign',
    scope,
  });

  const initialDimensionSelection = useMemo(
    () =>
      buildIndependentDimensionSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        TABLE_BY_CAMPAIGN_DIMENSION_KEYS,
        (sources) => ({ [TABLE_BY_CAMPAIGN_DIMENSION_KEY]: [...sources] })
      ),
    [config, data]
  );

  const [dimensionSelection, setDimensionSelection] = useState<
    Record<string, DimensionSource[]>
  >(initialDimensionSelection);
  const dimensionSelectionRef = useRef(dimensionSelection);
  dimensionSelectionRef.current = dimensionSelection;

  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  const campaignsRanges: PickersShortcutsItem<[Dayjs, Dayjs]>[] = useMemo(
    () =>
      campaigns.map((campaign) => ({
        label: campaign.name,
        getValue: () => [dayjs(campaign.start_date), dayjs(campaign.end_date)],
      })),
    [campaigns]
  );

  useEffect(() => {
    setDimensionSelection(initialDimensionSelection);
  }, [initialDimensionSelection]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setDimensionSelection((previousSelection) => {
      const syncedSelection = syncIndependentDimensionSelectionFromCatalog(
        data,
        TABLE_BY_CAMPAIGN_DIMENSION_KEYS,
        previousSelection
      );

      return areAxisSourceSelectionsEqual(
        dimensionSelectionToAxisSources(previousSelection),
        dimensionSelectionToAxisSources(syncedSelection)
      )
        ? previousSelection
        : syncedSelection;
    });
  }, [data]);

  const applyDimensionSelection = useCallback(
    (nextSelection: Record<string, DimensionSource[]>) => {
      const resolvedSelection = resolveIndependentDimensionSelectionFromCatalog(
        data,
        nextSelection,
        TABLE_BY_CAMPAIGN_DIMENSION_KEYS
      );
      const newConfig = setElementIndependentSourcesFromResolvedSources(
        resolvedSelection,
        TABLE_BY_CAMPAIGN_DIMENSION_KEYS,
        ensureConfigured(config)
      );
      setValidation(isValid(newConfig));
      onChange(newConfig);
    },
    [config, data, ensureConfigured, onChange]
  );

  const handleDateUpdate = (value: DateRange | null) => {
    const start = value === null ? new Date(0) : value[0];
    const end = value === null ? new Date(0) : value[1];
    const configuredConfig = ensureConfigured(config);
    const newConfig = setStartDate(
      addLocalOffsetAndFormatToLocalString(start.toISOString()),
      setEndDate(
        addLocalOffsetAndFormatToLocalString(end.toISOString()),
        configuredConfig
      )
    );
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const dataSourceErrors = validation?.[1]?.dataSource ?? [];
  const dataSourceHasError: ErrorStatus =
    submitCount > 0
      ? { tag: 'Submited', errors: dataSourceErrors }
      : { tag: 'Not submitted' };
  const dateRangeErrors = validation?.[1]?.dateRange ?? [];
  const dateRangeHasError = submitCount > 0 && dateRangeErrors.length > 0;

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialIndependentSelection={dimensionSelection}
        onIndependentDimensionSelect={(_dimensionKey, selected) => {
          const nextSelection = {
            ...dimensionSelectionRef.current,
            [TABLE_BY_CAMPAIGN_DIMENSION_KEY]: selected,
          };
          setDimensionSelection(nextSelection);
          applyDimensionSelection(nextSelection);
        }}
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <PlotConfigSection title="Période">
        <DateTimeRangePickerComponent
          format="DD/MM/YYYY"
          predefinedRanges={campaignsRanges}
          defaultValues={[
            new Date(getStartDate(config as TableByCampaign) ?? 'Z'),
            new Date(getEndDate(config as TableByCampaign) ?? 'Z'),
          ]}
          onChange={(value) => {
            handleDateUpdate(value);
          }}
          error={dateRangeHasError}
          helperText={dateRangeHasError ? dateRangeErrors.join('. ') : ''}
        />
      </PlotConfigSection>

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          dataSourceHasError.tag === 'Submited' &&
          dataSourceHasError.errors.length > 0
        }
      />
    </PlotConfigFormShell>
  );
}

export default TableByCampaignForm;
